import * as Sentry from '@sentry/react';

import pick from 'lodash/pick';

import {
  DELETE_BRANCH_DATA,
  EXCHANGE_TOKEN_FAILURE,
  EXCHANGE_TOKEN_REQUEST,
  EXCHANGE_TOKEN_SUCCESS,
  FORGOT_PASSWORD_FAILURE,
  FORGOT_PASSWORD_REQUESTED,
  FORGOT_PASSWORD_SUCCEEDED,
  GET_REGISTERED_USER_DATA_FAILURE,
  GET_REGISTERED_USER_DATA_REQUEST,
  GET_REGISTERED_USER_DATA_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT,
  SIGNUP_FAILURE,
  SIGNUP_REQUEST,
  SIGNUP_SUCCESS,
  SOCIAL_SIGNUP_FAILURE,
  SOCIAL_SIGNUP_REQUEST,
  SOCIAL_SIGNUP_SUCCESS,
  VERIFY_FAILURE,
  VERIFY_REQUEST,
  VERIFY_SUCCESS,
} from '../../actionConstants';

import {
  removePinnedBuildings,
  removeServiceData,
  removeTimezonesData,
  removeToken,
  removeUser,
  saveLastActivityTime,
  saveToken,
} from '../../utils';
import {
  getServiceData,
  getUserInfo,
  toggleIsNavigationShown,
} from '../Navigation/utils';
import { sendGA4Event } from '../Shared/utils/ga';
import { getVerificationData } from './utils';

import apiClient from '../../apiClient';
import { history } from '../../store';

const onLogin = (dispatch, token, redirectToAfterLogin) => {
  let redirectTo = redirectToAfterLogin || './';
  if (redirectTo === '/logout' || redirectTo === '/login') {
    redirectTo = './';
  }
  saveToken(token);
  apiClient.setToken(token);
  getUserInfo(dispatch, redirectTo);
};

export const getRegisteredUserData = (dispatch) =>
  dispatch({
    type: GET_REGISTERED_USER_DATA_REQUEST,
    payload: apiClient.auth.getRegisteredUser().then(
      (res) => {
        dispatch({
          type: GET_REGISTERED_USER_DATA_SUCCESS,
          res,
        });
        return res.data;
      },
      (err) =>
        dispatch({
          type: GET_REGISTERED_USER_DATA_FAILURE,
          err,
        }),
    ),
  });

const onSignupSuccess = async (
  dispatch,
  token,
  data,
  socialSignupData = {},
) => {
  apiClient.setToken(token);
  await getServiceData(dispatch);
  const user = await getRegisteredUserData(dispatch);
  const verificationData = getVerificationData(user);
  const state = { ...data, ...verificationData, ...socialSignupData };
  return history.replace('/verify', state);
};

export const login = (dispatch, data, redirectToAfterLogin) => {
  const { username, password } = data;
  return dispatch({
    type: LOGIN_REQUEST,
    isForm: true,
    payload: apiClient.auth.login(username, password).then(
      (res) => {
        const token = res?.data?.token;
        onLogin(dispatch, token, redirectToAfterLogin);
        sendGA4Event('login', {
          method: 'Carson username and password',
        });
        return dispatch({
          type: LOGIN_SUCCESS,
          token,
          successMessage: 'Welcome!',
        });
      },
      (err) => {
        dispatch({
          type: LOGIN_FAILURE,
          err,
        });
        return err;
      },
    ),
  });
};

export const logout = (dispatch) => {
  dispatch({ type: LOGOUT });
  toggleIsNavigationShown(dispatch, false);

  saveLastActivityTime();
  removeToken();
  removeUser();
  removeServiceData();
  removeTimezonesData();
  removePinnedBuildings();

  apiClient.setToken(null);
  Sentry.configureScope((scope) => {
    scope.setUser({});
  });
};

export const socialAuth = (dispatch, loginType, data, redirectToAfterLogin) =>
  dispatch({
    type: LOGIN_REQUEST,
    payload: apiClient.auth.socialLogin(loginType, data).then(
      (res) => {
        const token = res?.data?.token;
        onLogin(dispatch, token, redirectToAfterLogin);
        sendGA4Event('login', {
          method: loginType,
        });
        return dispatch({
          type: LOGIN_SUCCESS,
          token,
          successMessage: 'Welcome!',
        });
      },
      (err) => {
        dispatch({
          type: LOGIN_FAILURE,
          err,
        });
        return err;
      },
    ),
  });

export const socialSignup = (dispatch, type, accessToken, data) => {
  const reqData = { accessToken, ...data };
  return dispatch({
    type: SOCIAL_SIGNUP_REQUEST,
    payload: apiClient.auth.socialSignup(type, reqData).then(
      (res) => {
        dispatch({
          type: SOCIAL_SIGNUP_SUCCESS,
        });
        const { token } = res.data;
        const socialSignupData = { type, accessToken };
        sendGA4Event('sign_up', {
          method: type,
        });
        return onSignupSuccess(dispatch, token, data, socialSignupData);
      },
      (err) => {
        dispatch({
          type: SOCIAL_SIGNUP_FAILURE,
          err,
        });
        sendGA4Event('sign_up_failed', {
          method: type,
        });
      },
    ),
  });
};

export const onResetRequested = (dispatch, data) =>
  dispatch({
    type: FORGOT_PASSWORD_REQUESTED,
    isForm: true,
    payload: apiClient.auth.forgotPassword(data.username).then(
      (res) =>
        dispatch({
          type: FORGOT_PASSWORD_SUCCEEDED,
          payload: res,
          successMessage: 'Password Reset Requested, check your e-mail',
        }),
      (err) => {
        dispatch({
          type: FORGOT_PASSWORD_FAILURE,
          err,
        });
        return err;
      },
    ),
  });

export const signup = (dispatch, data) => {
  const reqData = pick(data, ['person', 'username', 'password', 'source']);
  return dispatch({
    type: SIGNUP_REQUEST,
    isForm: true,
    payload: apiClient.auth.signup(reqData).then(
      async (res) => {
        dispatch({
          type: SIGNUP_SUCCESS,
          res,
        });
        const { token } = res.data;
        sendGA4Event('sign_up', {
          method: data?.source,
        });
        return onSignupSuccess(dispatch, token, data);
      },
      (err) => {
        dispatch({
          type: SIGNUP_FAILURE,
          err,
        });
        return err;
      },
    ),
  });
};

export const onUserVerified = (dispatch, data) => {
  const {
    username,
    password,
    hasAdminAccess,
    type: socialLoginType,
    accessToken,
  } = data;
  if (hasAdminAccess && username && password) {
    return login(dispatch, { username, password });
  }
  if (hasAdminAccess && socialLoginType && accessToken) {
    return socialAuth(dispatch, socialLoginType, { accessToken });
  }
  if (!hasAdminAccess) {
    return history.replace('/support');
  }
  return history.replace('./');
};

export const verify = (dispatch, data) => {
  const reqData = pick(data, [
    'firstName',
    'lastName',
    'email',
    'phone_number',
  ]);
  return dispatch({
    type: VERIFY_REQUEST,
    isForm: true,
    payload: apiClient.auth.verify(reqData).then(
      () => {
        dispatch({ type: VERIFY_SUCCESS });
        dispatch({ type: DELETE_BRANCH_DATA });
        return onUserVerified(dispatch, data);
      },
      (err) => {
        dispatch({
          type: VERIFY_FAILURE,
          err,
        });
        return err;
      },
    ),
  });
};

export const exchangeToken = (dispatch, data) =>
  dispatch({
    type: EXCHANGE_TOKEN_REQUEST,
    payload: apiClient.auth.exchangeToken(data).then(
      (res) => {
        const token = res?.data?.token;
        saveToken(token);
        apiClient.setToken(token);
        dispatch({ type: EXCHANGE_TOKEN_SUCCESS, token });
        return token;
      },
      (err) => {
        dispatch({ type: EXCHANGE_TOKEN_FAILURE, err });
      },
    ),
  });
